import { componentCls } from "@faire/slate/lib/tracking";
import { tsv } from "@faire/slate/lib/tsv";
import { buildFormIds } from "@faire/slate/private/form/utils";
import { RadioBase } from "@faire/slate/private/radio/RadioBase";
import { RadioDescription } from "@faire/slate/private/radio/RadioDescription";
import { RadioLabel } from "@faire/slate/private/radio/RadioLabel";
import {
  IRadioPropsConstraints,
  IRadioWebProps,
} from "@faire/slate/private/radio/types";
import type { IText } from "@faire/slate/private/shared/types";
import React from "react";

import { RADIO_INPUT_UNIQUE_ID_PREFIX } from "./consts";

type IProps = {
  /**
   * Whether the radio is checked.
   *
   * @default false
   */
  checked?: boolean;
  /**
   * Whether the radio is disabled.
   *
   * @default false
   */
  disabled?: boolean;
  /**
   * The radio label.
   */
  label?: IText;
  /**
   * The radio description.
   */
  description?: IText;
  /**
   * Whether to give the radio a solid fill so it stays legible on non-white
   * surfaces, like images.
   *
   * @default false
   */
  showFill?: boolean;

  // DO NOT ADD any new prop without aligning with Slate team first
} & IRadioWebProps;

/**
 * Radios enable users to select one option from a list.
 *
 * See [migration guide](migration-guide.mdx) for more information.
 *
 * {@link https://www.notion.so/faire/Radio-2242efb5c25a80e0acc3c4bff1d737de | Slate/Radio}
 */
export const Radio: React.FC<IRadioPropsConstraints<IProps>> = ({
  id: idProp,
  label,
  description,
  showFill = false,
  ...props
}) => {
  const { id, helperId } = buildFormIds({
    prefix: RADIO_INPUT_UNIQUE_ID_PREFIX,
    idProp,
    label,
    helper: description,
  });
  return (
    <div
      className={radioStyle.cls({
        filled: showFill,
        className: componentCls(),
      })}
      data-component-name="Slate/Radio"
    >
      <RadioBase
        {...props}
        aria-describedby={helperId}
        hasLabel={!!label}
        id={id}
      />
      {label ? (
        <RadioLabel data-disabled={props.disabled} htmlFor={id}>
          {label}
        </RadioLabel>
      ) : null}
      {description ? (
        <RadioDescription data-disabled={props.disabled} id={helperId}>
          {description}
        </RadioDescription>
      ) : null}
    </div>
  );
};

const radioStyle = tsv({
  base: "fs-radio",
  variants: {
    filled: { true: "fs-radio--filled" },
  },
});
